import { defineStore } from 'pinia'

import { useAsyncRequest } from '@/composables/useAsyncRequest'
import { useProductStore } from '@/stores/product'

export const useProductPriceAvailabilityStore = defineStore('productPriceAvailability', () => {
    const route = useRoute()
    const productStore = useProductStore()
    const items = ref([])
    const startDates = ref([])

    const { loading, error, request } = useAsyncRequest()

    const loadStartDates = async () => {
        const id = productStore.item?.id
        if (!id) return

        const response = await request('get', `/api/products/${id}/price-availability/start-dates`, {})

        if (response) {
            const payload = response?.data ?? response
            const list = Array.isArray(payload?.data) ? payload.data : Array.isArray(payload) ? payload : []
            const dates = list
                .map((item) => {
                    if (typeof item === 'string') return item
                    if (!item || typeof item !== 'object') return null
                    return item.startDate ?? item.start_date ?? null
                })
                .filter(Boolean)

            startDates.value = [...new Set(dates)]
        }
    }

    const loadByStartDate = async (startDate) => {
        const id = productStore.item?.id
        if (!id) return

        if (!startDate) {
            items.value = []
            return
        }

        const response = await request('get', `/api/products/${id}/price-availability/by-start-dates`, {
            start_date: startDate
        })

        if (response) {
            const payload = response?.data ?? response
            const list = Array.isArray(payload?.data) ? payload.data : Array.isArray(payload) ? payload : []
            items.value = list
        }
    }

    const availableDates = computed(() => startDates.value)

    const getAvailableNights = (startDate) => {
        if (!startDate) return []

        return [
            ...new Set(
                items.value
                    .filter((item) => item.startDate === startDate)
                    .map((item) => Number(item.nights))
                    .filter((night) => Number.isFinite(night) && night > 0)
            )
        ].sort((a, b) => a - b)
    }

    const getAvailabilityItem = (startDate, nights) => {
        if (!startDate || !nights) return null

        return items.value.find((item) => item.startDate === startDate && Number(item.nights) === Number(nights)) ?? null
    }

    const initialize = async () => {
        await loadStartDates()

        if (route.query.start_date) {
            await loadByStartDate(route.query.start_date)
        }
    }

    return {
        initialize,
        items,
        startDates,
        loading,
        error,
        availableDates,
        loadStartDates,
        loadByStartDate,
        getAvailableNights,
        getAvailabilityItem
    }
})
